import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@services/notificationService';
import { useAuthStore } from '@stores/authStore';
import { adapterConfig } from '@/api/config';
import { parseNotification } from '@/types/current-notification';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (principal: string) => ['notifications', principal] as const,
};
export function useNotifications() {
  const principal = useAuthStore((state) => state.session?.principalId);
  return useQuery({
    queryKey: notificationKeys.list(principal ?? ''),
    queryFn: ({ signal }) => notificationService.getNotifications(signal),
    enabled: !!principal,
    retry: false,
    staleTime: 15000,
  });
}
export function useMarkNotificationRead() {
  const principal = useAuthStore((state) => state.session?.principalId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: notificationKeys.list(principal ?? ''),
      }),
  });
}
export function useMarkAllNotificationsRead() {
  const principal = useAuthStore((state) => state.session?.principalId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => notificationService.markAllAsRead(ids),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: notificationKeys.list(principal ?? ''),
      }),
  });
}
export function useNotificationsSSE() {
  const principal = useAuthStore((state) => state.session?.principalId);
  const account = useAuthStore((state) => state.session?.accountId);
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('Connecting live updates…');
  useEffect(() => {
    if (!principal || !account) return;
    if (typeof EventSource === 'undefined') {
      setStatus('Live updates unavailable. Refresh notifications to check.');
      return;
    }
    let active = true,
      last = 0n;
    let refresh: ReturnType<typeof setTimeout> | undefined;
    const source = new EventSource(adapterConfig.baseURL + '/events?after=0', {
      withCredentials: true,
    });
    source.onopen = () => {
      if (active) setStatus('Live updates connected');
    };
    source.addEventListener('notification', (event: MessageEvent<string>) => {
      if (!active || useAuthStore.getState().session?.principalId !== principal)
        return;
      try {
        const notification = parseNotification(event.data, account);
        if (event.lastEventId !== notification.notificationId)
          throw new Error('Invalid event cursor.');
        const id = BigInt(notification.notificationId);
        if (id <= last) return;
        last = id;
        if (refresh) return;
        refresh = setTimeout(() => {
          refresh = undefined;
          if (
            !active ||
            useAuthStore.getState().session?.principalId !== principal
          )
            return;
          void queryClient.invalidateQueries({
            queryKey: notificationKeys.list(principal),
          });
          // Events announce changes; authoritative reads still decide outcomes.
          for (const domain of [
            'current-projects',
            'current-finance',
            'current-profiles',
            'current-worker',
            'current-skill-requests',
          ])
            void queryClient.invalidateQueries({
              queryKey: [domain, principal],
            });
          void queryClient.invalidateQueries({
            queryKey: ['current-disputes'],
          });
          void queryClient.invalidateQueries({
            queryKey: ['onboarding', 'catalog'],
          });
        }, 200);
      } catch {
        source.close();
        setStatus(
          'Invalid live update received. Refresh notifications to check.'
        );
      }
    });
    source.onerror = () => {
      if (!active) return;
      setStatus(
        source.readyState === EventSource.CLOSED
          ? 'Live updates stopped. Refresh or reload to reconnect.'
          : 'Live updates reconnecting. You can still refresh notifications.'
      );
      void queryClient.invalidateQueries({
        queryKey: notificationKeys.list(principal),
      });
    };
    return () => {
      active = false;
      source.close();
      if (refresh) clearTimeout(refresh);
    };
  }, [principal, account, queryClient]);
  return status;
}
